/* eslint-disable func-style */
/* eslint-disable react/no-array-index-key */

import type { A11yTextType } from '../../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type {
    FixedEcomPromotion,
    PercentageEcomPromotion,
} from '../../../../common/models/domain/ecommerce/promotion/promotion';
import type { PriceType } from '../../../../common/models/priceData/priceData';
import type { ProductPricePresentationData } from '../../../../ecommerce/components/ProductPresentationSpecs/ProductPresentationSpecs';
import type { PrepurchaseProductPriceData } from './PrepurchaseProductPriceLabels.types';

import { Helpers } from '../../../../../utility/helpers';
import { pickProductPricePromotion } from '../../../../common/components/atoms/ProductPrice/ProductPricePromotionPicker';
import { EcomProductPrice } from '../../../../ecommerce/components/EcomProductPrice';
import {
    getProductComparativePriceText,
    ProductInfoProperty,
} from '../../../../ecommerce/components/ProductInfoProperty';
import {
    getProductPriceLabelForSpec,
    type ProductPresentationLabel,
    type ProductPricePresentationSpec,
} from '../../../../ecommerce/components/ProductPresentationSpecs';
import getProductPrimaryPriceLabel from '../../../../ecommerce/components/ProductPrimaryPriceLabel/ProductPrimaryPriceLabel';
import renderProductPriceGroups, {
    defineProductPriceGroup,
} from '../../../../ecommerce/utils/renderProductPriceGroups';
import { getPrePurchaseProductPrimaryComparativePriceLabel } from './PrepurchasePrimaryComparativePriceLabels';
import {
    getAdjustedPercentageForHistoricalPricePromo,
    getComparativePriceForHigherThanHistoricalPrice,
    getPiecePriceForHigherThanHistoricalPrice,
} from './PrepurchaseProductPriceLabels.utils';

export type PrepurchaseProductPriceLabelParameters = {
    product: PrepurchaseProductPriceData;
    priceType: PriceType;
    a11yTextType: A11yTextType;
    isB2B: boolean;
    isUserMember: boolean;
};

export const getPrepurchaseProductPriceA11yText = (
    params: PrepurchaseProductPriceLabelParameters,
) => {
    const prepurchaseProductPriceLabels = getPrePurchaseProductPriceGroups(params);
    const primaryPriceLabel = prepurchaseProductPriceLabels.primary.a11y;
    const secondaryPriceLabel = prepurchaseProductPriceLabels.secondary.a11y;

    const a11yText = [primaryPriceLabel, secondaryPriceLabel].filter(Boolean).join(', ');

    return a11yText;
};

const getPrePurchaseProductPrimaryPriceLabel = ({
    product,
    priceType,
    a11yTextType,
    isUserMember,
}: Omit<PrepurchaseProductPriceLabelParameters, 'isB2B'>): ProductPresentationLabel | null => {
    const picked = pickProductPricePromotion({
        promo: product?.promotion,
        checkUserMemberContext: false,
    });

    switch (picked.type) {
        case 'fixed': {
            // show fixed promo as regular if promo price is higher than historical price
            if (picked.promotion.higherThanHistoricalPrice) {
                const finalPiecePrice = getPiecePriceForHigherThanHistoricalPrice({
                    isMemberPrice: picked.promotion.isMemberPrice,
                    newPiecePrice: product.promotionPriceData,
                    piecePrice: product.priceData,
                    isUserMember,
                });

                return {
                    a11y: EcomProductPrice.getRegularPriceTexts(finalPiecePrice, priceType, false)
                        .a11yText,
                    render: () => (
                        <EcomProductPrice.Regular
                            displayLowerPrice={false} // we dont show lower price for higher than historical price promos
                            piecePrice={finalPiecePrice}
                            priceType={priceType}
                            a11yType={a11yTextType}
                        />
                    ),
                };
            }

            return getProductPrimaryPriceLabel({
                product,
                a11yTextType,
                priceType,
                isUserMember,
            });
        }
        case 'percentage': {
            // show percentage promo as regular if promo price is higher than historical price
            if (picked.promotion.higherThanHistoricalPrice) {
                const finalPiecePrice = getPiecePriceForHigherThanHistoricalPrice({
                    isMemberPrice: picked.promotion.isMemberPrice,
                    newPiecePrice: product.promotionPriceData,
                    piecePrice: product.priceData,
                    isUserMember,
                });

                return {
                    a11y: EcomProductPrice.getRegularPriceTexts(finalPiecePrice, priceType, false)
                        .a11yText,
                    render: () => (
                        <EcomProductPrice.Regular
                            displayLowerPrice={false} // we dont show lower price for higher than historical price promos
                            piecePrice={finalPiecePrice}
                            priceType={priceType}
                            a11yType={a11yTextType}
                        />
                    ),
                };
            }

            // if there is historical price but promo IS NOT higher than historical price, we need to check for adjustedPercantage to show percentage adjusted for historical price
            const finalPercentage = getAdjustedPercentageForHistoricalPricePromo({
                percentage: picked.promotion.percentageDiscount,
                adjustedPercentage: picked.promotion.adjustedPercentageDiscount,
                historicalPriceData: product.historicalPriceData,
            });

            return {
                a11y: EcomProductPrice.getPercentagePromotionTexts({
                    isMemberPrice: picked.promotion.isMemberPrice,
                    percentage: finalPercentage,
                    newPiecePrice: product.promotionPriceData,
                    priceType,
                }).a11yText,
                render: () => (
                    <EcomProductPrice.PercentagePromotion
                        promo={picked.promotion}
                        newPiecePrice={product.promotionPriceData}
                        percentage={finalPercentage}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        }
        case 'multiBuy': {
            return {
                a11y: EcomProductPrice.getMultiBuyPromotionTexts(picked.promotion, priceType)
                    .a11yText,
                render: () => (
                    <EcomProductPrice.MultiBuyPromotion
                        promo={picked.promotion}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        }
        case 'edlp': {
            return getProductPrimaryPriceLabel({
                product,
                a11yTextType,
                priceType,
                isUserMember,
            });
        }
        case 'notApplicable': {
            return {
                a11y: EcomProductPrice.getRegularPriceTexts(
                    product.priceData,
                    priceType,
                    product.displayLowerPrice,
                ).a11yText,
                render: () => (
                    <EcomProductPrice.Regular
                        displayLowerPrice={product.displayLowerPrice}
                        piecePrice={product.priceData}
                        priceType={priceType}
                        a11yType={a11yTextType}
                    />
                ),
            };
        }

        default: {
            if (product.promotionPriceData) {
                return {
                    a11y: EcomProductPrice.getFlatDiscountPriceTexts(
                        product.promotionPriceData,
                        priceType,
                    ).a11yText,
                    render: () =>
                        product.promotionPriceData && (
                            <EcomProductPrice.FlatDiscount
                                newPiecePrice={product.promotionPriceData}
                                priceType={priceType}
                                a11yType={a11yTextType}
                            />
                        ),
                };
            }

            if (product.priceData) {
                return {
                    a11y: EcomProductPrice.getRegularPriceTexts(
                        product.priceData,
                        priceType,
                        product.displayLowerPrice,
                    ).a11yText,
                    render: () =>
                        product.priceData && (
                            <EcomProductPrice.Regular
                                displayLowerPrice={product.displayLowerPrice}
                                piecePrice={product.priceData}
                                priceType={priceType}
                                a11yType={a11yTextType}
                            />
                        ),
                };
            }

            return null;
        }
    }
};

type ProductPromoPriceHigherThanHistoricalLabelForSpecParams = {
    product: ProductPricePresentationData & { displayLowerPrice: boolean };
    promo: PercentageEcomPromotion | FixedEcomPromotion;
    priceType: PriceType;
    a11yType: A11yTextType;
    isUserMember: boolean;
};

const getProductPromoPriceHigherThanHistoricalLabelForSpec =
    ({
        product,
        promo,
        priceType,
        a11yType,
        isUserMember,
    }: ProductPromoPriceHigherThanHistoricalLabelForSpecParams) =>
    (spec: ProductPricePresentationSpec): ProductPresentationLabel | null => {
        switch (spec) {
            case 'comparativePrice': {
                const priceData =
                    product.comparativePrice &&
                    getComparativePriceForHigherThanHistoricalPrice({
                        isUserMember,
                        isPromoMemberPrice: promo.isMemberPrice,
                        comparativePriceData: product.comparativePrice.priceData,
                        promoComparativePriceData: promo.comparativePriceData,
                    });

                const texts =
                    priceData &&
                    product.comparativePrice &&
                    getProductComparativePriceText({
                        priceData,
                        unit: product.comparativePrice.unit,
                        postfix: product.comparativePrice.postfix,
                        fallbackText: product.comparativePrice.fallbackText,
                        priceType,
                        basedOn: 'default',
                    });

                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Black a11yText={texts.a11y} a11yType={a11yType}>
                            {texts.visual}
                        </ProductInfoProperty.Black>
                    ),
                };
            }
            case 'recycleFee':
            case 'primaryPrice':
            case 'excludingTax':
                return getProductPriceLabelForSpec({
                    product,
                    priceType,
                    a11yType,
                    isUserMember,
                })(spec);

            // These specs are not shown for higher-than-historical PIL promotions
            case 'maxPromoUsage':
            case 'originalPrice':
            case 'promoComparativePrice':
            case 'historicalPrice':
                return null;
            default:
                return null;
        }
    };

type SpecRendererMap = Partial<{
    [K in ProductPricePresentationSpec]: (
        params: PrepurchaseProductPriceLabelParameters,
    ) => ProductPresentationLabel | null;
}>;

export const getPrePurchaseProductSecondaryPriceLabel =
    (params: PrepurchaseProductPriceLabelParameters) =>
    (
        spec: ProductPricePresentationSpec,
        specRenderers?: SpecRendererMap,
    ): ProductPresentationLabel | null => {
        const picked = pickProductPricePromotion({
            promo: params.product.promotion,
            checkUserMemberContext: false,
        });

        /* handle all specs for special case when promo is higher than historical price
            we need hide some fields since promos will be shown as black labels
            */
        if (picked.promotion?.higherThanHistoricalPrice) {
            switch (picked.type) {
                case 'fixed':
                case 'percentage': {
                    return getProductPromoPriceHigherThanHistoricalLabelForSpec({
                        product: params.product,
                        promo: picked.promotion,
                        priceType: params.priceType,
                        a11yType: params.a11yTextType,
                        isUserMember: params.isUserMember,
                    })(spec);
                }
                default:
                    return getProductPriceLabelForSpec({
                        product: params.product,
                        priceType: params.priceType,
                        a11yType: params.a11yTextType,
                        isUserMember: params.isUserMember,
                    })(spec);
            }
        } else {
            switch (spec) {
                case 'historicalPrice': {
                    // Try custom renderer for this spec
                    if (specRenderers?.historicalPrice) {
                        return specRenderers.historicalPrice(params);
                    }

                    // if not just default render
                    return getProductPriceLabelForSpec({
                        product: params.product,
                        priceType: params.priceType,
                        a11yType: params.a11yTextType,
                        isUserMember: params.isUserMember,
                    })(spec);
                }
                // when flags are gone this can be removed
                case 'promoComparativePrice': {
                    if (specRenderers?.promoComparativePrice) {
                        return specRenderers.promoComparativePrice(params);
                    }

                    return getProductPriceLabelForSpec({
                        product: params.product,
                        priceType: params.priceType,
                        a11yType: params.a11yTextType,
                        isUserMember: params.isUserMember,
                    })(spec);
                }
                case 'originalPrice':
                case 'comparativePrice': {
                    return getProductPriceLabelForSpec({
                        product: params.product,
                        priceType: params.priceType,
                        a11yType: params.a11yTextType,
                        isUserMember: params.isUserMember,
                    })(spec);
                }
                default:
                    if (specRenderers && specRenderers[spec]) {
                        return specRenderers[spec]!(params);
                    }
                    return getProductPriceLabelForSpec({
                        product: params.product,
                        priceType: params.priceType,
                        a11yType: params.a11yTextType,
                        isUserMember: params.isUserMember,
                    })(spec);
            }
        }
    };

export const prePurchaseProductPricePresentationLogic = {
    getPrimaryPricePresentation: getPrePurchaseProductPrimaryPriceLabel,
    getSecondaryPricePresentation: getPrePurchaseProductSecondaryPriceLabel,
};

export const getPrePurchaseProductPriceGroups = (params: PrepurchaseProductPriceLabelParameters) =>
    renderProductPriceGroups(
        () => `${params.product.identifier}`,
        [
            defineProductPriceGroup({
                type: 'primary',
                specSet: ['primaryPrice', 'primaryComparativePrice'],
                createLabel: (spec) => {
                    switch (spec) {
                        case 'primaryPrice':
                            return getPrePurchaseProductPrimaryPriceLabel({
                                product: params.product,
                                priceType: params.priceType,
                                a11yTextType: params.a11yTextType,
                                isUserMember: params.isUserMember,
                            });
                        case 'primaryComparativePrice':
                            return getPrePurchaseProductPrimaryComparativePriceLabel({
                                product: params.product,
                                priceType: params.priceType,
                                a11yTextType: params.a11yTextType,
                                isUserMember: params.isUserMember,
                            });

                        default:
                            return Helpers.exhaustiveCheck(spec);
                    }
                },
            }),
            defineProductPriceGroup({
                type: 'secondary',
                specSet: [
                    'maxPromoUsage',
                    'recycleFee',
                    'promoComparativePrice',
                    'originalPrice',
                    'comparativePrice',
                    'historicalPrice',
                    'excludingTax',
                    'primaryPrice',
                ],
                createLabel: (spec) => {
                    switch (spec) {
                        case 'recycleFee':
                        case 'comparativePrice':
                        case 'excludingTax':
                        case 'maxPromoUsage':
                        case 'originalPrice':
                        case 'primaryPrice':
                        case 'promoComparativePrice':
                        case 'historicalPrice':
                            return getPrePurchaseProductSecondaryPriceLabel({
                                product: params.product,
                                priceType: params.priceType,
                                a11yTextType: params.a11yTextType,
                                isB2B: params.isB2B,
                                isUserMember: params.isUserMember,
                            })(spec);
                        default:
                            return Helpers.exhaustiveCheck(spec);
                    }
                },
            }),
        ],
    );
